/**
 * PanoComponent — R3F component for interior 360° panorama display.
 *
 * Responsibilities:
 * 1. Subscribes to PanoManager.onChange for pending-state updates.
 * 2. Manages a black-curtain overlay (position:fixed div on document.body).
 * 3. Runs a phase machine: idle → fading-out → loading → fading-in
 *    to guarantee a smooth black-fade on every enter/switch/exit transition.
 * 4. Loads equirectangular textures via THREE.TextureLoader with a URL cache.
 * 5. Renders an inverted sphere (side=BackSide) with toneMapped=false to show
 *    correct sRGB colours — bypasses ACES Filmic that would distort pano images.
 * 6. Calls onEnterPano / onExitPano behind the curtain so camera + model
 *    changes are never visible to the user.
 *
 * @see docs/ims-engine-api/12-feature-pano-measures.md
 * @see PanoManager.ts
 * @see docs/ims-engine-api/01-architecture.md — On-Demand Render-Loop pattern
 */

import { useState, useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { PanoManager } from './PanoManager';
import type { PanoState } from '../api/types/pano';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Duration of the fade-to-black and fade-to-clear phases in seconds. */
const FADE_DURATION = 0.3;

/** z-index that sits above the environment overlay (which also uses 9999). */
const OVERLAY_Z = 10000;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PanoComponentProps {
  manager: PanoManager;
  /**
   * Called once behind the curtain when switching FROM inactive TO active.
   * Adjust camera constraints and hide the vehicle model here.
   */
  onEnterPano: () => void;
  /**
   * Called once behind the curtain when switching FROM active TO inactive.
   * Restore camera constraints and show the vehicle model here.
   */
  onExitPano: () => void;
}

// ---------------------------------------------------------------------------
// PanoComponent
// ---------------------------------------------------------------------------

type Phase = 'idle' | 'fading-out' | 'loading' | 'fading-in';

export function PanoComponent({ manager, onEnterPano, onExitPano }: PanoComponentProps) {
  const pendingStateRef = useRef<PanoState>(manager.getState());
  const liveStateRef = useRef<PanoState>(manager.getState());

  const phaseRef = useRef<Phase>('idle');
  const fadeClock = useRef(0);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);
  const textureCacheRef = useRef(new Map<string, THREE.Texture>());
  const invalidateRef = useRef<(() => void) | null>(null);

  const [liveTexture, setLiveTexture] = useState<THREE.Texture | null>(null);

  // Keep invalidate ref fresh every render.
  const invalidate = useThree((s) => s.invalidate);
  invalidateRef.current = invalidate;

  // ── Subscribe to PanoManager ───────────────────────────────────────────────

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional: wires PanoManager callback to R3F invalidate bridge
    manager.onChange = (state) => {
      pendingStateRef.current = state;
      invalidate(); // wake frameloop
    };
    return () => {
      manager.onChange = undefined;
    };
  }, [manager, invalidate]);

  // ── Overlay div (full-viewport black curtain) ──────────────────────────────

  useEffect(() => {
    const div = document.createElement('div');
    div.style.cssText = `position:fixed;inset:0;background:#000;opacity:0;pointer-events:none;z-index:${OVERLAY_Z};`;
    document.body.appendChild(div);
    overlayRef.current = div;
    // Capture ref value at the top of the effect so the cleanup closure
    // always references the Map instance that was current when this effect ran.
    const textureCache = textureCacheRef.current;

    return () => {
      document.body.removeChild(div);
      overlayRef.current = null;
      // Dispose all cached textures on unmount.
      textureCache.forEach((t) => t.dispose());
      textureCache.clear();
    };
  }, []);

  // ── Texture loader (ref for stable identity across renders) ───────────────

  const loadTexture = useRef((url: string, onDone: () => void) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    phaseRef.current = 'loading';

    const cache = textureCacheRef.current;

    /** Dispose every cached texture except `keepUrl` to release VRAM.
     *  Equirectangular pano textures are large (32–128 MB each on GPU) and
     *  accumulate quickly on mobile. iOS Safari will crash if too many are
     *  held simultaneously, so we keep at most one texture in the cache. */
    const evictOthers = (keepUrl: string) => {
      cache.forEach((t, key) => {
        if (key !== keepUrl) {
          t.dispose();
          cache.delete(key);
        }
      });
    };

    if (cache.has(url)) {
      const tex = cache.get(url)!;
      evictOthers(url);
      setLiveTexture(tex);
      isLoadingRef.current = false;
      onDone();
      invalidateRef.current?.();
      return;
    }

    new THREE.TextureLoader().load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        // Equirectangular images on a BackSide sphere appear horizontally
        // mirrored (left ↔ right) because the normals face inward, flipping
        // the U axis relative to a FrontSide sphere.  Flipping repeat.x=-1
        // (with offset.x=1 to keep the origin in-bounds) corrects the mirror
        // without touching the geometry.
        tex.wrapS = THREE.RepeatWrapping;
        tex.repeat.set(-1, 1);
        tex.offset.set(1, 0);
        cache.set(url, tex);
        evictOthers(url); // release all previously seen panos from VRAM
        setLiveTexture(tex);
        isLoadingRef.current = false;
        onDone();
        invalidateRef.current?.();
      },
      undefined,
      (err) => {
        console.warn('[PanoComponent] Texture load failed:', url, err);
        isLoadingRef.current = false;
        // Fade back in even on error (will show nothing)
        phaseRef.current = 'fading-in';
        fadeClock.current = 0;
        invalidateRef.current?.();
      },
    );
  });

  // ── Phase machine ──────────────────────────────────────────────────────────

  useFrame((_, delta) => {
    const pending = pendingStateRef.current;
    const live = liveStateRef.current;
    const overlay = overlayRef.current;
    const phase = phaseRef.current;

    const pendingKey = `${pending.active}|${pending.url ?? ''}`;
    const liveKey = `${live.active}|${live.url ?? ''}`;

    if (phase === 'idle') {
      if (pendingKey !== liveKey) {
        // Kick off the black-curtain transition
        phaseRef.current = 'fading-out';
        fadeClock.current = 0;
        invalidate();
      }
      return;
    }

    if (phase === 'fading-out') {
      fadeClock.current += delta;
      const opacity = Math.min(fadeClock.current / FADE_DURATION, 1);
      if (overlay) overlay.style.opacity = String(opacity);
      invalidate();

      if (opacity >= 1 && !isLoadingRef.current) {
        // ── Behind the curtain: commit state and apply side-effects ──────────
        const wasActive = live.active;
        liveStateRef.current = { ...pending };

        // Camera + model side-effects only on mode switch, not URL-only changes
        if (pending.active && !wasActive) {
          onEnterPano();
        } else if (!pending.active && wasActive) {
          setLiveTexture(null);
          onExitPano();
        }

        if (pending.active && pending.url) {
          loadTexture.current(pending.url, () => {
            phaseRef.current = 'fading-in';
            fadeClock.current = 0;
          });
        } else {
          // Exiting or no URL: no texture to load, go straight to fade-in
          phaseRef.current = 'fading-in';
          fadeClock.current = 0;
        }
      }
      return;
    }

    if (phase === 'loading') {
      if (overlay) overlay.style.opacity = '1';
      invalidate();
      return;
    }

    if (phase === 'fading-in') {
      fadeClock.current += delta;
      const opacity = Math.max(1 - fadeClock.current / FADE_DURATION, 0);
      if (overlay) overlay.style.opacity = String(opacity);
      if (opacity <= 0) {
        if (overlay) overlay.style.opacity = '0';
        phaseRef.current = 'idle';
      }
      invalidate();
    }
  });

  // ── Render: inverted sphere with equirectangular texture ──────────────────

  if (!liveTexture) return null;

  return (
    <mesh>
      <sphereGeometry args={[50, 64, 32]} />
      {/* toneMapped=false: pano images are sRGB-correct renders — bypass ACES */}
      <meshBasicMaterial map={liveTexture} side={THREE.BackSide} toneMapped={false} />
    </mesh>
  );
}
